// ---------------------------------------------------------------------------
// SEC-025 Phase 2 — GDPR Art. 20 DSAR Export Pipeline CQRS event catalog
// + pure builders
// ---------------------------------------------------------------------------
// Story SEC-025: "A data subject can request a portable export of their
// personal data (GDPR Art. 20 — right to data portability). The pipeline
// MUST record request intake, compilation outcome, delivery to the
// subject, and any terminal failure as immutable CQRS events so the
// regulator-evidence dashboard, the compliance audit-runner, and the
// 30-day-clock reconciler converge on a single source of truth."
//
// Sibling catalog `sec-005-gdpr-dsar-event-catalog.ts` covers the
// *cross-domain provider fan-out* tier (CRM, forms, page-builder, email,
// analytics, onboarding, billing, community, courses, webinar). SEC-025
// is the *export pipeline lifecycle* tier — it sits ABOVE the provider
// fan-out and describes the four lifecycle anchors the DSAR export
// pipeline emits independent of how many providers contributed slices:
//
//   requested → compiled → delivered
//                       ↘ failed (terminal)
//
// Both catalogs intentionally coexist — SEC-005 narrows on the per-domain
// emit/complete/fail axis (fan-out fidelity), SEC-025 narrows on the
// request lifecycle axis (subject-facing SLA + regulator anchor). The
// reconciler joins them on `requestId`.
//
// Per CLAUDE.md Governance Rule #6 (atomic events — one fact per event)
// and Rule #7 (event catalog before implementation), this file pre-
// catalogues every DSAR-export-pipeline domain event the future export
// service + delivery worker + audit-runner MUST emit into
// `customer_events`.
//
// What this file IS:
//   - The single source of truth for the four `gdpr.dsar_export.*`
//     event-type strings.
//   - The closed `deliveryChannel` vocabulary (`email` | `download_link`)
//     pinned verbatim from the SEC-025 story acceptance criterion.
//   - The closed `failureReason` vocabulary so DLQ + compliance
//     escalation routes per-reason without string parsing (compliance
//     events MUST NOT be acked per CLAUDE.md anti-pattern #6).
//   - Typed Zod payload schemas + TypeScript types so emitters,
//     audit-runner, compliance dashboard, and DLQ consumers cannot drift
//     on the payload shape.
//   - Pure builders — accept caller primitives, validate at the
//     boundary, emit ISO-8601 canonical timestamps, return the typed
//     envelope. No I/O. No DB writes. No env access. The writer (export
//     service or delivery worker) owns actual emission to
//     `customer_events`.
//   - Runtime narrowing guards so DLQ / audit-runner reject unknown
//     event types at the boundary.
//   - Discriminated union mapping each event type to its payload so
//     consumers `switch (event.type)` exhaustively without any-casting.
//
// What this file is NOT:
//   - It does not implement the export pipeline — the future
//     export-service worker does.
//   - It does not declare Drizzle tables.
//   - It does not change the cross-domain provider matrix (SEC-005).
//
// Mirrors the BIL-017 / SEC-024 / SEC-005 Phase 2 vocabulary-first
// pattern (closed enums + Zod schemas + pure builders + tests).
// ---------------------------------------------------------------------------

import { z } from "zod";

// ---------------------------------------------------------------------------
// Event type catalog — frozen string constants
// ---------------------------------------------------------------------------

export const SEC_025_DSAR_EXPORT_REQUESTED_EVENT =
  "gdpr.dsar_export_requested@v1" as const;
export const SEC_025_DSAR_EXPORT_COMPILED_EVENT =
  "gdpr.dsar_export_compiled@v1" as const;
export const SEC_025_DSAR_EXPORT_DELIVERED_EVENT =
  "gdpr.dsar_export_delivered@v1" as const;
export const SEC_025_DSAR_EXPORT_FAILED_EVENT =
  "gdpr.dsar_export_failed@v1" as const;

/**
 * Closed catalog of SEC-025 CQRS event types. Versioned with `@v1`
 * suffix per platform convention (Governance Rule #7). One business
 * fact per event (Governance Rule #6).
 *
 *  - `gdpr.dsar_export_requested@v1` — data-subject filed an Art. 20
 *    portable-export request. Starts the 30-day GDPR clock.
 *  - `gdpr.dsar_export_compiled@v1` — pipeline finished assembling the
 *    portable bundle (every provider slice merged into the archive).
 *    Carries inventory metadata (`recordCount`, `sizeBytes`) so the
 *    compliance dashboard can surface bundle size without joining R2.
 *  - `gdpr.dsar_export_delivered@v1` — bundle reached the subject via
 *    one of the catalogued delivery channels. Closes the 30-day clock
 *    on the happy path.
 *  - `gdpr.dsar_export_failed@v1` — pipeline terminally failed. Closed
 *    `reason` vocab lets the audit-runner route to the operator-alert
 *    channel without string parsing.
 */
export const SEC_025_DSAR_EXPORT_EVENT_TYPES = [
  SEC_025_DSAR_EXPORT_REQUESTED_EVENT,
  SEC_025_DSAR_EXPORT_COMPILED_EVENT,
  SEC_025_DSAR_EXPORT_DELIVERED_EVENT,
  SEC_025_DSAR_EXPORT_FAILED_EVENT,
] as const;

export type Sec025DsarExportEventType =
  (typeof SEC_025_DSAR_EXPORT_EVENT_TYPES)[number];

export function isSec025DsarExportEventType(
  value: unknown,
): value is Sec025DsarExportEventType {
  return (
    typeof value === "string" &&
    (SEC_025_DSAR_EXPORT_EVENT_TYPES as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Delivery channel vocabulary
// ---------------------------------------------------------------------------
//
//  - `email`         — bundle attached to (or signed-URL-linked from) an
//    email to the data-subject's verified address.
//  - `download_link` — short-lived signed-download URL surfaced in the
//    in-product DSAR portal; subject must be authenticated to retrieve.
//
// Locked to the two channels pinned by the SEC-025 story acceptance
// criterion. Adding a third channel (e.g. `dpo_handover`) MUST extend
// this tuple in lockstep with the delivery worker.

export const SEC_025_DSAR_EXPORT_DELIVERY_CHANNEL_VOCAB = [
  "email",
  "download_link",
] as const;

export type Sec025DsarExportDeliveryChannel =
  (typeof SEC_025_DSAR_EXPORT_DELIVERY_CHANNEL_VOCAB)[number];

export function isSec025DsarExportDeliveryChannel(
  value: unknown,
): value is Sec025DsarExportDeliveryChannel {
  return (
    typeof value === "string" &&
    (SEC_025_DSAR_EXPORT_DELIVERY_CHANNEL_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

// ---------------------------------------------------------------------------
// Failure reason vocabulary
// ---------------------------------------------------------------------------
//
//  - `provider_timeout`        — one or more domain providers (SEC-005)
//    missed their SLO budget past the retry envelope.
//  - `provider_error`          — a domain provider threw an error the
//    retry budget could not recover from.
//  - `storage_upload_failed`   — the compiled archive could not land in
//    R2 (object-store outage / quota exhaustion).
//  - `delivery_failed`         — bundle compiled successfully but
//    delivery to the subject failed (email bounce / signed-URL token
//    issuance failure).
//  - `subject_not_found`       — the supplied data-subject id did not
//    resolve to any record after identity verification.
//  - `request_withdrawn`       — the data-subject withdrew the request
//    before the bundle was delivered (terminal — not an error condition,
//    but lifecycle-closing).
//  - `internal_error`          — unhandled error in the export pipeline;
//    the audit-runner escalates to on-call.

export const SEC_025_DSAR_EXPORT_FAILURE_REASON_VOCAB = [
  "provider_timeout",
  "provider_error",
  "storage_upload_failed",
  "delivery_failed",
  "subject_not_found",
  "request_withdrawn",
  "internal_error",
] as const;

export type Sec025DsarExportFailureReason =
  (typeof SEC_025_DSAR_EXPORT_FAILURE_REASON_VOCAB)[number];

export function isSec025DsarExportFailureReason(
  value: unknown,
): value is Sec025DsarExportFailureReason {
  return (
    typeof value === "string" &&
    (SEC_025_DSAR_EXPORT_FAILURE_REASON_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

// ---------------------------------------------------------------------------
// Shared base — every event carries these
// ---------------------------------------------------------------------------

/**
 * Common identity fields every SEC-025 event carries. The `requestId`
 * pairs every related event in the lifecycle; the reconciler joins
 * SEC-025 events back to SEC-005 provider events on this id.
 */
export interface Sec025DsarExportEventBase {
  /** Tenant scope — every event MUST be tenant-scoped. */
  tenantId: string;
  /** Stable DSAR request id; pairs every related event in the lifecycle. */
  requestId: string;
  /** ISO-8601 with offset. Set by the builder via `nowIso()`. */
  occurredAt: string;
}

// ---------------------------------------------------------------------------
// Per-event Zod payload schemas + TypeScript types
// ---------------------------------------------------------------------------

const isoTimestampSchema = z.string().datetime({ offset: true });

export const Sec025DsarExportRequestedPayloadSchema = z.object({
  type: z.literal(SEC_025_DSAR_EXPORT_REQUESTED_EVENT),
  tenantId: z.string().min(1),
  requestId: z.string().min(1),
  dataSubjectUserId: z.string().min(1),
  occurredAt: isoTimestampSchema,
});
export type Sec025DsarExportRequestedPayload = z.infer<
  typeof Sec025DsarExportRequestedPayloadSchema
>;

export const Sec025DsarExportCompiledPayloadSchema = z.object({
  type: z.literal(SEC_025_DSAR_EXPORT_COMPILED_EVENT),
  tenantId: z.string().min(1),
  requestId: z.string().min(1),
  /**
   * Total record count across every provider slice merged into the
   * portable bundle. MUST be a non-negative integer — zero is a valid
   * terminal state (subject existed but produced no records).
   */
  recordCount: z.number().int().nonnegative(),
  /**
   * Total compiled bundle size in bytes. MUST be a non-negative integer
   * — zero is illegal (an empty archive still carries a manifest).
   * Capped at 2^53-1 via JS-safe integer to avoid silent truncation in
   * downstream consumers.
   */
  sizeBytes: z.number().int().positive().safe(),
  occurredAt: isoTimestampSchema,
});
export type Sec025DsarExportCompiledPayload = z.infer<
  typeof Sec025DsarExportCompiledPayloadSchema
>;

export const Sec025DsarExportDeliveredPayloadSchema = z.object({
  type: z.literal(SEC_025_DSAR_EXPORT_DELIVERED_EVENT),
  tenantId: z.string().min(1),
  requestId: z.string().min(1),
  deliveryChannel: z.enum(SEC_025_DSAR_EXPORT_DELIVERY_CHANNEL_VOCAB),
  occurredAt: isoTimestampSchema,
});
export type Sec025DsarExportDeliveredPayload = z.infer<
  typeof Sec025DsarExportDeliveredPayloadSchema
>;

export const Sec025DsarExportFailedPayloadSchema = z.object({
  type: z.literal(SEC_025_DSAR_EXPORT_FAILED_EVENT),
  tenantId: z.string().min(1),
  requestId: z.string().min(1),
  reason: z.enum(SEC_025_DSAR_EXPORT_FAILURE_REASON_VOCAB),
  occurredAt: isoTimestampSchema,
});
export type Sec025DsarExportFailedPayload = z.infer<
  typeof Sec025DsarExportFailedPayloadSchema
>;

// ---------------------------------------------------------------------------
// Discriminated union — consumers `switch (event.type)` for exhaustive narrow
// ---------------------------------------------------------------------------

export type Sec025DsarExportEvent =
  | {
      type: typeof SEC_025_DSAR_EXPORT_REQUESTED_EVENT;
      payload: Sec025DsarExportRequestedPayload;
    }
  | {
      type: typeof SEC_025_DSAR_EXPORT_COMPILED_EVENT;
      payload: Sec025DsarExportCompiledPayload;
    }
  | {
      type: typeof SEC_025_DSAR_EXPORT_DELIVERED_EVENT;
      payload: Sec025DsarExportDeliveredPayload;
    }
  | {
      type: typeof SEC_025_DSAR_EXPORT_FAILED_EVENT;
      payload: Sec025DsarExportFailedPayload;
    };

// ---------------------------------------------------------------------------
// Internal helpers (private to the catalog)
// ---------------------------------------------------------------------------

function assertNonEmpty(value: string, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `SEC-025 DSAR export emitter: ${field} must be non-empty string`,
    );
  }
  return value;
}

function assertNonNegativeInteger(value: number, field: string): number {
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < 0 ||
    !Number.isSafeInteger(value)
  ) {
    throw new Error(
      `SEC-025 DSAR export emitter: ${field} must be safe non-negative integer (got ${String(value)})`,
    );
  }
  return value;
}

function assertPositiveInteger(value: number, field: string): number {
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < 1 ||
    !Number.isSafeInteger(value)
  ) {
    throw new Error(
      `SEC-025 DSAR export emitter: ${field} must be safe positive integer (got ${String(value)})`,
    );
  }
  return value;
}

function nowIso(now: Date | undefined): string {
  const stamp = now ?? new Date();
  if (Number.isNaN(stamp.getTime())) {
    throw new Error("SEC-025 DSAR export emitter: invalid Date supplied");
  }
  return stamp.toISOString();
}

// ---------------------------------------------------------------------------
// Pure builders — accept caller primitives, return typed envelope
// ---------------------------------------------------------------------------

export interface BuildDsarExportRequestedInput {
  tenantId: string;
  requestId: string;
  dataSubjectUserId: string;
  now?: Date;
}

export function buildDsarExportRequestedEvent(
  input: BuildDsarExportRequestedInput,
): Sec025DsarExportRequestedPayload {
  return Sec025DsarExportRequestedPayloadSchema.parse({
    type: SEC_025_DSAR_EXPORT_REQUESTED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    requestId: assertNonEmpty(input.requestId, "requestId"),
    dataSubjectUserId: assertNonEmpty(
      input.dataSubjectUserId,
      "dataSubjectUserId",
    ),
    occurredAt: nowIso(input.now),
  });
}

export interface BuildDsarExportCompiledInput {
  tenantId: string;
  requestId: string;
  recordCount: number;
  sizeBytes: number;
  now?: Date;
}

export function buildDsarExportCompiledEvent(
  input: BuildDsarExportCompiledInput,
): Sec025DsarExportCompiledPayload {
  return Sec025DsarExportCompiledPayloadSchema.parse({
    type: SEC_025_DSAR_EXPORT_COMPILED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    requestId: assertNonEmpty(input.requestId, "requestId"),
    recordCount: assertNonNegativeInteger(input.recordCount, "recordCount"),
    sizeBytes: assertPositiveInteger(input.sizeBytes, "sizeBytes"),
    occurredAt: nowIso(input.now),
  });
}

export interface BuildDsarExportDeliveredInput {
  tenantId: string;
  requestId: string;
  deliveryChannel: Sec025DsarExportDeliveryChannel;
  now?: Date;
}

export function buildDsarExportDeliveredEvent(
  input: BuildDsarExportDeliveredInput,
): Sec025DsarExportDeliveredPayload {
  if (!isSec025DsarExportDeliveryChannel(input.deliveryChannel)) {
    throw new Error(
      `SEC-025 DSAR export emitter: deliveryChannel must be one of ${SEC_025_DSAR_EXPORT_DELIVERY_CHANNEL_VOCAB.join("|")} (got ${String(input.deliveryChannel)})`,
    );
  }
  return Sec025DsarExportDeliveredPayloadSchema.parse({
    type: SEC_025_DSAR_EXPORT_DELIVERED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    requestId: assertNonEmpty(input.requestId, "requestId"),
    deliveryChannel: input.deliveryChannel,
    occurredAt: nowIso(input.now),
  });
}

export interface BuildDsarExportFailedInput {
  tenantId: string;
  requestId: string;
  reason: Sec025DsarExportFailureReason;
  now?: Date;
}

export function buildDsarExportFailedEvent(
  input: BuildDsarExportFailedInput,
): Sec025DsarExportFailedPayload {
  if (!isSec025DsarExportFailureReason(input.reason)) {
    throw new Error(
      `SEC-025 DSAR export emitter: reason must be one of ${SEC_025_DSAR_EXPORT_FAILURE_REASON_VOCAB.join("|")} (got ${String(input.reason)})`,
    );
  }
  return Sec025DsarExportFailedPayloadSchema.parse({
    type: SEC_025_DSAR_EXPORT_FAILED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    requestId: assertNonEmpty(input.requestId, "requestId"),
    reason: input.reason,
    occurredAt: nowIso(input.now),
  });
}

// ---------------------------------------------------------------------------
// Canonical JSON serializer (ISO timestamps + stable key ordering)
// ---------------------------------------------------------------------------
//
// `customer_events.payload` is `jsonb`, and PostgreSQL `jsonb` is
// key-order-insensitive on read. However, downstream evidence anchors
// (SOC 2 audit-runner, regulator-evidence dashboard, R2 archive
// fingerprints) hash the serialized JSON — so we MUST emit a canonical
// key order at the boundary to keep hashes stable across re-runs.
//
// Canonical rules:
//   1. Keys serialized in lexicographic (Unicode codepoint) order.
//   2. All timestamps are ISO-8601 strings with offset (already guaranteed
//      by the builders above + the Zod `.datetime({offset:true})` guard).
//   3. No trailing whitespace; no pretty-printing.
//
// Returns the canonical JSON string. Throws if the payload contains a
// value JSON.stringify cannot serialize (BigInt, functions, symbols).

export function canonicalizeSec025Payload(
  payload: Sec025DsarExportEvent["payload"],
): string {
  return JSON.stringify(payload, canonicalReplacer);
}

function canonicalReplacer(_key: string, value: unknown): unknown {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }
  const sortedEntries = Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = (value as Record<string, unknown>)[k];
      return acc;
    }, {});
  return sortedEntries;
}
